import React, { useEffect, useState } from 'react';
import { CocoEvent } from '@cotera/client/app/etc';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { BroadcastEventType } from '@cotera/api';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading, toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  ResourceSelectionModel,
  ResourceType,
} from '../resources/selection/resource-selection.model';
import { WorkflowModel } from './workflow.model';

const renameWorkflowParamsSchema = z.object({
  name: z.string().min(1),
  // Optional: the caller (e.g. a row kebab menu) already knows exactly which
  // workflow it targets, so it can pass the id directly rather than relying on
  // the shared `ResourceSelectionModel` being targeted at ask/execute time.
  workflowId: z.string().optional(),
});

type Params = z.infer<typeof renameWorkflowParamsSchema>;

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

type RenameWorkflowActionClient = {
  resource: {
    workflow: Pick<TenantedClient['resource']['workflow'], 'update'>;
  };
};

export class RenameWorkflowAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Rename Workflow';
  description = 'Rename a workflow';
  inputSchema = renameWorkflowParamsSchema;
  group = 'Workflow';
  icon = 'waypoints';

  private _lastRenamedWorkflowId: string | null = null;

  constructor(
    private readonly client: RenameWorkflowActionClient,
    private readonly modalManager: ModalManager,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTargetWorkflowId(context) !== null;
  }

  getPriority(context: ApplicableContext): number {
    if (this.getTargetWorkflowId(context) !== null) {
      return 20;
    }

    return 0;
  }

  shouldAsk(): boolean {
    return true;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const workflowId = payload.workflowId ?? this.getTargetWorkflowId(context);
    if (workflowId === null || workflowId === undefined) {
      return err([{ t: 'error', message: 'No workflow in scope' }]);
    }

    const name = payload.name.trim();
    const result = await this.client.resource.workflow.update({
      id: workflowId,
      name,
    });

    if (result.isErr()) {
      const msg =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to rename workflow';
      toast.error(msg);
      return err([{ t: 'error', message: msg }]);
    }

    const workflowModel = context.targetedModelOfType(WorkflowModel);
    if (workflowModel !== null && workflowModel.workflowId === workflowId) {
      workflowModel.setNameForSave(name);
    }

    // Restream the resource lists (triggers page, folder contents, etc.) that
    // are backed by `ResourceUpdated` watchables so the renamed workflow shows
    // its new name without a manual refetch.
    emit(BroadcastEventType.ResourceUpdated, {
      orgId: this.orgId,
      resourceType: 'workflow',
      resourceId: workflowId,
    });

    const selection = context.targetedModelOfType(ResourceSelectionModel);
    if (selection !== null) {
      context.markAsNotTargeted(selection);
    }

    this._lastRenamedWorkflowId = workflowId;

    toast.success('Workflow renamed');
    return ok({});
  }

  track(): ActionTrackMetadata | null {
    if (this._lastRenamedWorkflowId === null) {
      return null;
    }
    return {
      event: CocoEvent.WorkflowRenamed,
      properties: { workflowId: this._lastRenamedWorkflowId },
    };
  }

  ask(
    params: Partial<Params>,
    context: ExecuteContext
  ): AskResponse<Params, { t: string; message?: string }[]> {
    const workflowId = params.workflowId ?? this.getTargetWorkflowId(context);
    const initialName =
      params.name ?? this.getInitialWorkflowNameFromContext(context);
    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async () => {
          await onCancel();
          this.modalManager.pop();
        };

        this.modalManager.push({
          view: () => (
            <RenameWorkflowAskView
              workflowId={workflowId}
              initialName={initialName}
              onRespond={async (nextParams) => {
                await onRespond(nextParams);
                this.modalManager.pop();
              }}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }

  private getTargetWorkflowId(context: ApplicableContext): string | null {
    if (context.isInScope(ResourceSelectionModel)) {
      const selection = context.getInScopeModelsOfType(ResourceSelectionModel);
      if (
        selection.length === 1 &&
        selection[0]?.selections.length === 1 &&
        selection[0]?.getResourceIdsForType(ResourceType.Workflow).length === 1
      ) {
        return (
          selection[0]?.getResourceIdsForType(ResourceType.Workflow)[0] ?? null
        );
      }
    }

    const workflowModel = context.targetedModelOfType(WorkflowModel);
    if (workflowModel !== null) {
      return workflowModel.workflowId;
    }

    return null;
  }

  private getInitialWorkflowNameFromContext(
    context: ExecuteContext
  ): string | undefined {
    if (context.isInScope(ResourceSelectionModel)) {
      const selection = context.getInScopeModelsOfType(ResourceSelectionModel);
      if (
        selection.length === 1 &&
        selection[0]?.selections.length === 1 &&
        selection[0]?.getResourceForType(ResourceType.Workflow) !== undefined
      ) {
        return selection[0]?.getResourceForType(ResourceType.Workflow)?.name;
      }
    }

    const workflowModel = context.targetedModelOfType(WorkflowModel);
    if (workflowModel !== null) {
      return workflowModel.getNameForSave();
    }

    return undefined;
  }
}

const RenameWorkflowAskView: React.FC<{
  workflowId: string | null;
  initialName?: string;
  onRespond: (params: Params) => Promise<void>;
  onCancel: () => Promise<void>;
}> = ({ workflowId, initialName, onRespond, onCancel }) => {
  const [name, setName] = useState(initialName ?? '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialName !== undefined) {
      setName(initialName);
    }
  }, [initialName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0 || loading || workflowId === null) {
      return;
    }
    setLoading(true);
    // Thread the resolved workflow id back through so `execute` acts on the
    // exact workflow the modal was opened for, independent of selection state.
    await onRespond({ name: trimmed, workflowId });
    setLoading(false);
  };

  return (
    <ModalPageView
      title="Rename workflow"
      subtitle="Choose a new name for this workflow"
      className="px-4 py-4"
    >
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-2">
          <label htmlFor="workflow-name" className="text-sm font-medium">
            Workflow name
          </label>
          <Input
            id="workflow-name"
            autoFocus
            placeholder="Workflow name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void onCancel()}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              loading || workflowId === null || name.trim().length === 0
            }
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Rename'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
